import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { authService } from './Services/authService';
import { clearUserSession, persistUserSession } from '../Utils/AuthUtils';
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
    skipAuthInterceptor?: boolean;
  }

  export interface AxiosRequestConfig {
    _retry?: boolean;
    skipAuthInterceptor?: boolean;
  }
}

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5079/api',
  withCredentials: true,
});

const sendLogoutRequest = () => {
  if (!api.defaults.baseURL) {
    return;
  }

  const logoutUrl = `${api.defaults.baseURL}/account/logout`;

  axios.post(logoutUrl, undefined, {
    withCredentials: true,
    headers: {
      'X-Tenant-Domain': window.location.host,
    },
    validateStatus: () => true,
  }).catch(() => {
    // Ignore logout errors; we still clear local session state.
  });
};

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers['X-Tenant-Domain'] = window.location.host;
  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  sendLogoutRequest();
  clearUserSession();
  toast.info('Sua sessão expirou. Faça login novamente.');
  window.location.href = '/login';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(error);
    }
    const status = error.response?.status;
    const isAuthEndpoint = originalRequest.url?.includes('/auth/refresh-token');

    if (status === 401 && !originalRequest._retry && !originalRequest.skipAuthInterceptor && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshedUser = await authService.refreshToken();
        persistUserSession(refreshedUser);

        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
